import { useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { FilterToolbarItemId } from 'src/components/Filters';
import { CurrencyFilterMultiSelector } from 'src/components/Selectors/CurrencyFilterSelector';
import { useAppContext } from 'src/contexts/AppContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { DateRangeSelector } from 'src/core/POS/DateRangeSelector';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import {
  MARKET_PLACE_TO_CID,
  MARKETPLACE_SALE_PAYMENT_STATE_TO_CID,
  YES_NO_ENUM_FILTER_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import { PurchaseDateRangePresetNames } from 'src/utils/dateTimeUtils';
import { FromYesNoEnum, ToYesNoEnum } from 'src/utils/eventQueryUtils';
import {
  Feature,
  Marketplace,
  MarketplacePaymentQuery,
} from 'src/WebApiController';

export const useMarketplacePaymentsFilters = () => {
  const { loginContext } = useAppContext();
  const { tempQuery, setTempQuery } =
    useFilterQueryContext<MarketplacePaymentQuery>();

  const hasMarketplacePaymentDiscrepancyFeature = useUserHasFeature(
    Feature.MarketplacePaymentDiscrepancy
  );

  return useMemo(() => {
    return {
      filters: [
        {
          titleContentId: ContentId.Payment,
          type: 'row',
          items: [
            {
              labelContentId: ContentId.PaymentDate,
              filterId: 'paymentDates' as FilterToolbarItemId,
              filterQueryKeys: ['paymentDates'] as FilterToolbarItemId[],
              filterItem: (
                <DateRangeSelector
                  presetNames={PurchaseDateRangePresetNames}
                  exactPresetNames={PurchaseDateRangePresetNames}
                  trailingPresetNames={PurchaseDateRangePresetNames}
                  value={tempQuery.paymentDates}
                  onBlur={({ value }) =>
                    setTempQuery({
                      ...tempQuery,
                      paymentDates: value,
                    })
                  }
                  useRelativePresets
                />
              ),
            },
            {
              labelContentId: ContentId.HasUnmappedSales,
              filterId: 'hasPaymentLineUnmappedToSale' as FilterToolbarItemId,
              filterQueryKeys: [
                'hasPaymentLineUnmappedToSale',
              ] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(tempQuery.hasPaymentLineUnmappedToSale)}
                  placeholderText={ContentId.All}
                  enableEmptySelection
                  onChange={(yesNoEnumValue) => {
                    if (
                      yesNoEnumValue !==
                      ToYesNoEnum(tempQuery.hasPaymentLineUnmappedToSale)
                    ) {
                      setTempQuery({
                        ...tempQuery,
                        hasPaymentLineUnmappedToSale:
                          FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                />
              ),
            },
            {
              labelContentId: ContentId.HasUnlinkedChargesCredits,
              filterId:
                'hasPaymentLineUnmappedToSaleLineItem' as FilterToolbarItemId,
              filterQueryKeys: [
                'hasPaymentLineUnmappedToSaleLineItem',
              ] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(
                    tempQuery.hasPaymentLineUnmappedToSaleLineItem
                  )}
                  placeholderText={ContentId.All}
                  enableEmptySelection
                  onChange={(yesNoEnumValue) => {
                    if (
                      yesNoEnumValue !==
                      ToYesNoEnum(
                        tempQuery.hasPaymentLineUnmappedToSaleLineItem
                      )
                    ) {
                      setTempQuery({
                        ...tempQuery,
                        hasPaymentLineUnmappedToSaleLineItem:
                          FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                />
              ),
            },
            {
              filterId: 'marketplace' as FilterToolbarItemId,
              labelContentId: ContentId.Marketplace,
              filterQueryKeys: ['marketplace'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={tempQuery.marketplace}
                  placeholderText={ContentId.AllMerchants}
                  enableEmptySelection
                  onChange={(marketplace) => {
                    if (marketplace !== tempQuery.marketplace) {
                      setTempQuery({
                        ...tempQuery,
                        marketplace,
                      });
                    }
                  }}
                  valueOptionsContent={(
                    loginContext?.user?.activeAccount?.marketplaceSettings?.map(
                      (m) => m.mkp
                    ) ?? [Marketplace.StubHub]
                  )
                    .concat(Marketplace.Offline)
                    .reduce(
                      (cur, m) => {
                        cur[m] = MARKET_PLACE_TO_CID[m];
                        return cur;
                      },
                      {} as Record<Marketplace, string>
                    )}
                />
              ),
            },
            {
              filterId: 'marketplacePaymentState' as FilterToolbarItemId,
              labelContentId: ContentId.PaymentState,
              filterQueryKeys: [
                'marketplacePaymentState',
              ] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={tempQuery.marketplacePaymentState}
                  enableEmptySelection
                  onChange={(marketplacePaymentState) => {
                    if (
                      marketplacePaymentState !==
                      tempQuery.marketplacePaymentState
                    ) {
                      setTempQuery({
                        ...tempQuery,
                        marketplacePaymentState,
                      });
                    }
                  }}
                  valueOptionsContent={MARKETPLACE_SALE_PAYMENT_STATE_TO_CID}
                />
              ),
            },
            {
              filterId: 'currencyCodes' as FilterToolbarItemId,
              labelContentId: ContentId.Currency,
              filterQueryKeys: ['currencyCodes'] as FilterToolbarItemId[],
              filterItem: (
                <CurrencyFilterMultiSelector
                  values={tempQuery.currencyCodes ?? []}
                  triggerProps={{ style: { width: '100%' } }}
                  placeholderText={ContentId.All}
                  onChange={({ newValues: newCurrencyCodes }) => {
                    if (!isEqual(tempQuery.currencyCodes, newCurrencyCodes)) {
                      setTempQuery({
                        ...tempQuery,
                        currencyCodes: newCurrencyCodes?.length
                          ? newCurrencyCodes
                          : null,
                      });
                    }
                  }}
                  enableEmptySelection
                />
              ),
            },
          ],
        },
        ...(hasMarketplacePaymentDiscrepancyFeature
          ? [
              {
                titleContentId: ContentId.TotalDiscrepancy,
                type: 'row',
                items: [
                  {
                    labelContentId: ContentId.HasPaymentTotalDiscrepancy,
                    filterId:
                      'hasPaymentTotalDiscrepancy' as FilterToolbarItemId,
                    filterQueryKeys: [
                      'hasPaymentTotalDiscrepancy',
                    ] as FilterToolbarItemId[],
                    filterItem: (
                      <PosEnumSelect
                        style={{ width: '100%' }}
                        value={ToYesNoEnum(
                          tempQuery.hasPaymentTotalDiscrepancy
                        )}
                        placeholderText={ContentId.All}
                        enableEmptySelection
                        onChange={(yesNoEnumValue) => {
                          if (
                            yesNoEnumValue !==
                            ToYesNoEnum(tempQuery.hasPaymentTotalDiscrepancy)
                          ) {
                            setTempQuery({
                              ...tempQuery,
                              hasPaymentTotalDiscrepancy:
                                FromYesNoEnum(yesNoEnumValue),
                            });
                          }
                        }}
                        valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                      />
                    ),
                  },
                  {
                    labelContentId: ContentId.HasProceedsTotalDiscrepancy,
                    filterId:
                      'hasProceedsTotalDiscrepancy' as FilterToolbarItemId,
                    filterQueryKeys: [
                      'hasProceedsTotalDiscrepancy',
                    ] as FilterToolbarItemId[],
                    filterItem: (
                      <PosEnumSelect
                        style={{ width: '100%' }}
                        value={ToYesNoEnum(
                          tempQuery.hasProceedsTotalDiscrepancy
                        )}
                        placeholderText={ContentId.All}
                        enableEmptySelection
                        onChange={(yesNoEnumValue) => {
                          if (
                            yesNoEnumValue !==
                            ToYesNoEnum(tempQuery.hasProceedsTotalDiscrepancy)
                          ) {
                            setTempQuery({
                              ...tempQuery,
                              hasProceedsTotalDiscrepancy:
                                FromYesNoEnum(yesNoEnumValue),
                            });
                          }
                        }}
                        valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                      />
                    ),
                  },
                  {
                    labelContentId: ContentId.HasChargeTotalDiscrepancy,
                    filterId:
                      'hasChargeTotalDiscrepancy' as FilterToolbarItemId,
                    filterQueryKeys: [
                      'hasChargeTotalDiscrepancy',
                    ] as FilterToolbarItemId[],
                    filterItem: (
                      <PosEnumSelect
                        style={{ width: '100%' }}
                        value={ToYesNoEnum(tempQuery.hasChargeTotalDiscrepancy)}
                        placeholderText={ContentId.All}
                        enableEmptySelection
                        onChange={(yesNoEnumValue) => {
                          if (
                            yesNoEnumValue !==
                            ToYesNoEnum(tempQuery.hasChargeTotalDiscrepancy)
                          ) {
                            setTempQuery({
                              ...tempQuery,
                              hasChargeTotalDiscrepancy:
                                FromYesNoEnum(yesNoEnumValue),
                            });
                          }
                        }}
                        valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                      />
                    ),
                  },
                  {
                    labelContentId: ContentId.HasCreditTotalDiscrepancy,
                    filterId:
                      'hasCreditTotalDiscrepancy' as FilterToolbarItemId,
                    filterQueryKeys: [
                      'hasCreditTotalDiscrepancy',
                    ] as FilterToolbarItemId[],
                    filterItem: (
                      <PosEnumSelect
                        style={{ width: '100%' }}
                        value={ToYesNoEnum(tempQuery.hasCreditTotalDiscrepancy)}
                        placeholderText={ContentId.All}
                        enableEmptySelection
                        onChange={(yesNoEnumValue) => {
                          if (
                            yesNoEnumValue !==
                            ToYesNoEnum(tempQuery.hasCreditTotalDiscrepancy)
                          ) {
                            setTempQuery({
                              ...tempQuery,
                              hasCreditTotalDiscrepancy:
                                FromYesNoEnum(yesNoEnumValue),
                            });
                          }
                        }}
                        valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                      />
                    ),
                  },
                ],
              },
            ]
          : []),
      ],
    };
  }, [
    hasMarketplacePaymentDiscrepancyFeature,
    loginContext?.user?.activeAccount?.marketplaceSettings,
    setTempQuery,
    tempQuery,
  ]);
};
